/**
 * Shared formatting + parsing helpers for the journalist tool.
 *
 * Keep these pure (no React, no DOM). They're imported by both display
 * components and tests.
 */

import type { CVRParticipantRole } from '../types'

const DA = 'da-DK'

/** Strip non-digits from a CVR / forretningsnøgle for stable comparisons. */
export function normalizeCvr(raw: string | number | null | undefined): string {
  return String(raw ?? '').replace(/\D/g, '')
}

/**
 * Format a number as a Danish percentage string ("51,5 %"). Returns
 * `null` for null/undefined/NaN so callers can render fallbacks.
 */
export function formatPercent(value: number | null | undefined): string | null {
  if (value === null || value === undefined || Number.isNaN(value)) return null
  return (
    new Intl.NumberFormat(DA, {
      maximumFractionDigits: 2,
      minimumFractionDigits: 0,
    }).format(value) + ' %'
  )
}

/**
 * Format a CVR ownership stake: single percent or min–max interval (en-dash).
 * Matches ejerskabsgraf edge labels when `max` is set.
 */
export function formatPercentRange(
  min: number | null | undefined,
  max?: number | null | undefined
): string | null {
  const lo = formatPercent(min)
  if (!lo) return null
  if (max === null || max === undefined || Number.isNaN(max)) return lo
  if (Math.abs(max - (min ?? max)) <= 0.005) return lo
  const hi = formatPercent(max)
  if (!hi) return lo
  return `${lo}–${hi}`
}

/**
 * Compact stake label for overview strips: integers, single trailing %.
 * Pair with {@link formatPercentRange} as hover `title` for full precision.
 */
export function formatPercentRangeCompact(
  min: number | null | undefined,
  max?: number | null | undefined,
): string | null {
  if (min === null || min === undefined || Number.isNaN(min)) return null
  const minR = Math.round(min)
  if (max === null || max === undefined || Number.isNaN(max) || Math.abs(max - min) <= 0.005) {
    return `${minR} %`
  }
  const maxR = Math.round(max)
  if (minR === maxR) return `${minR} %`
  return `${minR}–${maxR} %`
}

export interface OwnershipStakeLabels {
  compact: string
  full: string
}

/** Compact + full stake labels (strip overview). */
export function formatOwnershipStakeLabels(
  min: number | null | undefined,
  max?: number | null | undefined,
  underFive?: boolean,
): OwnershipStakeLabels | null {
  if (underFive) {
    const label = 'under 5 %'
    return { compact: label, full: label }
  }
  const full = formatPercentRange(min, max)
  const compact = formatPercentRangeCompact(min, max)
  if (!full || !compact) return null
  return { compact, full }
}

/** DD.MM.YY for compact period labels in overview strips. */
export function formatDateCompact(raw: string | null | undefined): string | null {
  if (!raw || isCvrUnknownPeriodDate(raw)) return null
  const d = new Date(raw)
  if (Number.isNaN(d.getTime())) return null
  const day = String(d.getDate()).padStart(2, '0')
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const year = String(d.getFullYear()).slice(-2)
  return `${day}.${month}.${year}`
}

/** Compact from–to period, e.g. `15.01.20–01.03.24`. */
export function formatPeriodCompact(
  from: string | null | undefined,
  to: string | null | undefined,
): string | null {
  const f = formatDateCompact(from)
  const t = formatDateCompact(to)
  if (f && t) return `${f}–${t}`
  if (f) return `fra ${f}`
  if (t) return `til ${t}`
  return null
}

/** Strip overview period — EJERREGISTER uses afhændelsesdato as end when set. */
export function formatParticipantStripPeriod(role: CVRParticipantRole): string | null {
  const end =
    role.organizationType === 'EJERREGISTER'
      ? (role.legalRegisterDisposalDate ?? role.validTo)
      : role.validTo
  return formatPeriodCompact(role.validFrom, end)
}

/**
 * Format legale ejere (EJERREGISTER) stakes: ERST registration band or “under 5 %”.
 */
export function formatLegalRegisterPercent(
  min: number | null | undefined,
  max?: number | null | undefined,
  underFive?: boolean
): string | null {
  if (underFive) return 'under 5 %'
  return formatPercentRange(min, max)
}

/** CVR sentinel gyldigFra meaning "unknown period start". */
export const CVR_UNKNOWN_PERIOD_START = '1903-01-01'

export function isCvrUnknownPeriodDate(raw: string | null | undefined): boolean {
  if (!raw) return false
  return raw.slice(0, 10) === CVR_UNKNOWN_PERIOD_START
}

/**
 * Format an ISO-8601 date as a Danish short date ("4. mar. 2026").
 * Falls back to the raw input if parsing fails.
 */
export function formatDate(raw: string | null | undefined): string | null {
  if (!raw || isCvrUnknownPeriodDate(raw)) return null
  try {
    return new Date(raw).toLocaleDateString(DA, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  } catch {
    return raw
  }
}

/** Parse server datetime stored as UTC (`Y-m-d H:i:s` or ISO). */
function parseServerUtc(raw: string): Date {
  const normalized = raw.includes('T') ? raw : `${raw.replace(' ', 'T')}Z`
  return new Date(normalized)
}

/** Server datetime as `DD.MM.YYYY HH:MM` in Europe/Copenhagen (CET/CEST). */
export function formatDateTimeDa(raw: string | null | undefined): string | null {
  if (!raw) return null
  const d = parseServerUtc(raw)
  if (Number.isNaN(d.getTime())) return null

  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/Copenhagen',
      day: 'numeric',
      month: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: false,
    })
      .formatToParts(d)
      .filter((p) => p.type !== 'literal')
      .map((p) => [p.type, p.value])
  )

  const pad = (n: number) => String(n).padStart(2, '0')
  const day = Number(parts.day)
  const month = Number(parts.month)
  const year = Number(parts.year)
  const hour = Number(parts.hour)
  const minute = Number(parts.minute)
  if ([day, month, year, hour, minute].some((n) => Number.isNaN(n))) return null

  return `${pad(day)}.${pad(month)}.${year} ${pad(hour)}:${pad(minute)}`
}

export interface CountrySignal {
  /** Two-letter ISO code if detected, otherwise null. */
  countryCode: string | null
  /** True when the detected country code is set and not DK. */
  isForeign: boolean
}

/**
 * Parse a country code out of the formatted address string emitted by
 * `DataParser::formatSingleAddressWithCountry` on the backend. The
 * address may look like one of:
 *
 *   "Some Street 1, 1000 København, Country: DK"
 *   "[PROTECTED] Country: AE"
 *   "[PROTECTED]"                         // no country
 *   "Some Street 1, 1000 København"       // no country
 *
 * Also recognises `Land: XX` (our display label) so parsing stays
 * consistent after `formatAddressCountryLabel`.
 */
export function detectCountry(address: string | undefined | null): CountrySignal {
  if (!address) return { countryCode: null, isForeign: false }
  const match = address.match(/(?:Country|Land):\s*([A-Z]{2})/i)
  if (!match) return { countryCode: null, isForeign: false }
  const code = match[1].toUpperCase()
  return { countryCode: code, isForeign: code !== 'DK' }
}

/** True when the participant is a VIRKSOMHED with an 8-digit Danish CVR. */
export function isDanishRegisteredCompany(participant: {
  enhedstype: string
  cvrNummer?: string | null
}): boolean {
  if (participant.enhedstype !== 'VIRKSOMHED') return false
  const digits = String(participant.cvrNummer ?? '').replace(/\D/g, '')
  return digits.length === 8
}

/**
 * ISO country code for the flag shown after a participant name, or null when
 * no flag should appear. Persons: foreign address. Companies: foreign address
 * (e.g. Greenland) or not registered in DK (no valid CVR).
 */
export function participantCountryFlagCode(participant: {
  enhedstype: string
  cvrNummer?: string | null
  adresse: string
}): string | null {
  const { countryCode, isForeign } = detectCountry(participant.adresse)
  if (!countryCode || countryCode === 'DK') return null

  if (participant.enhedstype === 'VIRKSOMHED') {
    if (isForeign) return countryCode
    if (!isDanishRegisteredCompany(participant)) return countryCode
    return null
  }

  return isForeign ? countryCode : null
}

/**
 * Format an address for display: ISO country tokens become localised names
 * (e.g. `Country: DK` / `Land: DK` → `Danmark`).
 */
export function formatAddressCountryLabel(raw: string | null | undefined): string {
  if (!raw) return ''
  return raw
    .replace(/\bCountry:\s*([A-Z]{2})\b/gi, (_, code: string) => countryDisplayName(code))
    .replace(/\bLand:\s*([A-Z]{2})\b/gi, (_, code: string) => countryDisplayName(code))
}

/**
 * One-line note when the address is confidential: keep country code from
 * the backend string (e.g. `[PROTECTED] Country: DK`) as **Land: DK**.
 */
export function formatProtectedAddressNote(address: string | null | undefined): string {
  const { countryCode } = detectCountry(address)
  if (countryCode) {
    return `Beskyttet adresse. ${countryDisplayName(countryCode)}`
  }
  return 'Beskyttet adresse.'
}

/**
 * Strip a trailing country suffix before rendering a separate foreign chip.
 * Accepts raw `Country:`/`Land:` tokens or an already-localised name via
 * `countryCode`.
 */
export function stripTrailingCountryFromAddress(
  addr: string,
  countryCode?: string | null
): string {
  let s = addr.replace(/,?\s*(Country|Land):\s*[A-Z]{2}\s*$/i, '').trim()
  if (countryCode) {
    const name = countryDisplayName(countryCode)
    const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    s = s.replace(new RegExp(`,?\\s*${escaped}\\s*$`, 'i'), '').trim()
  }
  return s
}

/** Company-like object with optional split postal fields from the API. */
export interface CompanyAddressFields {
  adresse?: string | null
  postnummer?: string | number | null
  postdistrikt?: string | null
}

/**
 * Full one-line company address. Uses `adresse` when it already contains
 * postnr./by; otherwise appends `postnummer` + `postdistrikt`.
 */
export function formatCompanyAddress(company: CompanyAddressFields): string {
  const street = formatAddressCountryLabel(company.adresse)
  if (!street) return ''

  const postal = [company.postnummer, company.postdistrikt]
    .filter((v) => v !== undefined && v !== null && String(v).trim() !== '')
    .map(String)
    .join(' ')
    .trim()

  if (!postal) return street

  const hasPostalInStreet = /\b\d{4}\b/.test(street)
  if (hasPostalInStreet) return street

  return `${street}, ${postal}`
}

/** ISO country flag code for a company profile header, or null when domestic. */
export function companyCountryFlagCode(
  company: CompanyAddressFields & { cvrNummer?: string | null },
): string | null {
  return participantCountryFlagCode({
    enhedstype: 'VIRKSOMHED',
    cvrNummer: company.cvrNummer,
    adresse: company.adresse ?? '',
  })
}

/**
 * Human-readable label for the composite CVR status. The CVR API returns
 * uppercased machine codes (often without spaces) — we translate known
 * `sammensatStatus` / `virksomhedsstatus` values and fall back to a
 * structured parse for `OPLØSTEFTER…` / `UNDER…` prefixes.
 */
const STATUS_LABEL: Record<string, string> = {
  NORMAL: 'Aktiv',
  AKTIV: 'Aktiv',
  UNDERKONKURS: 'Under konkurs',
  KONKURS: 'Konkurs',
  UNDERTVANGSOPLØSNING: 'Under tvangsopløsning',
  TVANGSOPLØST: 'Tvangsopløst',
  OPHØRT: 'Ophørt',
  OPLØSTEFTERKONKURS: 'Opløst efter konkurs',
  OPLØSTEFTERERKLÆRING: 'Opløst efter erklæring',
  OPLØSTEFTERFRIVILLIGLIKVIDATION: 'Opløst efter frivillig likvidation',
  OPLØSTEFTERFUSION: 'Opløst efter fusion',
  OPLØSTEFTERSPALTNING: 'Opløst efter spaltning',
  UNDERFRIVILLIGLIKVIDATION: 'Under frivillig likvidation',
  UNDERREASSUMERING: 'Under reassumering',
  UNDERREKONSTRUKTION: 'Under rekonstruktion',
  UDENRETSVIRKNING: 'Uden retsvirkning',
  SLETTET: 'Slettet',
}

/** Normalise upstream status strings for lookup (`OPLØST EFTER ERKLÆRING` → `OPLØSTEFTERERKLÆRING`). */
export function normalizeStatusKey(raw: string): string {
  return raw.toUpperCase().replace(/[\s_.-]/g, '')
}

const STATUS_FRAGMENT_LABEL: Record<string, string> = {
  ERKLÆRING: 'erklæring',
  KONKURS: 'konkurs',
  FRIVILLIGLIKVIDATION: 'frivillig likvidation',
  FUSION: 'fusion',
  SPALTNING: 'spaltning',
  TVANGSOPLØSNING: 'tvangsopløsning',
  REASSUMERING: 'reassumering',
  REKONSTRUKTION: 'rekonstruktion',
}

function humanizeStatusFragment(fragment: string): string {
  return STATUS_FRAGMENT_LABEL[fragment] ?? toTitleCase(fragment.replace(/_/g, ' '))
}

function formatUnknownStatusKey(key: string, raw: string): string {
  if (key.startsWith('OPLØSTEFTER')) {
    const rest = key.slice('OPLØSTEFTER'.length)
    if (rest) return `Opløst efter ${humanizeStatusFragment(rest)}`
  }
  if (key.startsWith('UNDER')) {
    const rest = key.slice('UNDER'.length)
    if (rest) return `Under ${humanizeStatusFragment(rest)}`
  }
  return toTitleCase(raw)
}

export function formatStatusLabel(raw: string | undefined | null): string {
  if (!raw) return 'Ukendt'
  const key = normalizeStatusKey(raw)
  return STATUS_LABEL[key] ?? formatUnknownStatusKey(key, raw)
}

const OPERATING_STATUS = new Set(['NORMAL', 'AKTIV'])

/** Statuses where the company is still registered but under a formal process. */
const DISTRESSED_STATUS = new Set([
  'UNDERKONKURS',
  'UNDERTVANGSOPLØSNING',
  'UNDERREKONSTRUKTION',
  'UNDERFRIVILLIGLIKVIDATION',
  'UNDERREASSUMERING',
])

export function isDistressedStatus(raw: string | undefined | null): boolean {
  if (!raw) return false
  return DISTRESSED_STATUS.has(normalizeStatusKey(raw))
}

/** Definitively ceased — opløst, ophørt, slettet, etc. (not under an open process). */
export function isDissolvedStatus(raw: string | undefined | null): boolean {
  if (!raw) return false
  const key = normalizeStatusKey(raw)
  if (OPERATING_STATUS.has(key)) return false
  if (DISTRESSED_STATUS.has(key)) return false
  return true
}

/**
 * True when the company has definitively ceased. Distressed statuses
 * (under konkurs/rekonstruktion) still count as operating for this check.
 */
export function isInactiveStatus(raw: string | undefined | null): boolean {
  return isDissolvedStatus(raw)
}

export type CompanyStatusBadgeTone = 'active' | 'neutral' | 'signal'

/** Badge tone for company status in profile UI. */
export function companyStatusBadgeTone(
  raw: string | undefined | null,
): CompanyStatusBadgeTone {
  if (!raw) return 'active'
  const key = normalizeStatusKey(raw)
  if (OPERATING_STATUS.has(key)) return 'active'
  if (DISTRESSED_STATUS.has(key)) return 'neutral'
  return 'signal'
}

/** Compact activity-row date (`DD.MM.YYYY`). */
export function formatActivityDateCompact(iso: string): string {
  const dateKey = iso.split('T')[0]
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateKey)
  if (!match) {
    return formatDate(dateKey) ?? iso
  }
  const [, year, month, day] = match
  return `${day}.${month}.${year}`
}

/** Compact two-line date for tight activity rows (e.g. `11. maj` + `'26`). */
export function formatActivityDateLines(iso: string): { when: string; year: string } {
  const dateKey = iso.split('T')[0]
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateKey)
  if (!match) {
    const formatted = formatDate(dateKey)
    const parts = formatted.split(' ')
    const year = parts.length > 1 ? (parts.pop() ?? '') : ''
    return { when: parts.join(' '), year: year ? `'${year.slice(-2)}` : '' }
  }
  const y = Number(match[1])
  const m = Number(match[2])
  const d = Number(match[3])
  const date = new Date(Date.UTC(y, m - 1, d))
  const when = new Intl.DateTimeFormat('da-DK', {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  }).format(date)
  return { when, year: `'${String(y).slice(-2)}` }
}

function toTitleCase(s: string): string {
  return s
    .toLowerCase()
    .split(/\s+/)
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : ''))
    .join(' ')
}

/** Human-readable role / function label (e.g. `DIREKTØR` → `Direktør`). */
export function formatRoleLabel(raw: string | null | undefined): string {
  if (!raw?.trim()) return ''
  return toTitleCase(raw.trim())
}

/**
 * Plain-language label for a person's role string from CVR.
 * Maps CVR technical codes to readable Danish, then falls back to title-case.
 *
 * Notable mapping: `EJERREGISTER` is the official CVR ejerbog (ownership
 * register). For non-experts "Ejer" conveys the same meaning without jargon.
 */
const PERSON_ROLE_MAP: Record<string, string> = {
  EJERREGISTER: 'Ejer',
}

export function formatPersonRoleLabel(raw: string | null | undefined): string {
  if (!raw?.trim()) return 'Anden rolle'
  const key = raw.trim().toUpperCase()
  return PERSON_ROLE_MAP[key] ?? formatRoleLabel(raw)
}

/** ISO-3166 alpha-2 → regional flag emoji. */
export function countryCodeToFlagEmoji(code: string): string {
  const upper = code.toUpperCase()
  if (!/^[A-Z]{2}$/.test(upper)) return ''
  return String.fromCodePoint(
    ...[...upper].map(c => 0x1f1e6 + c.charCodeAt(0) - 65),
  )
}

/** Localised country/region name for an ISO-3166 alpha-2 code. */
export function countryDisplayName(code: string, locale = 'da'): string {
  try {
    return new Intl.DisplayNames([locale], { type: 'region' }).of(code.toUpperCase()) ?? code
  } catch {
    return code
  }
}

/**
 * Days between two dates, treating both as UTC midnight to avoid DST
 * jitter. Returns null if either side cannot be parsed.
 */
export function daysSince(iso: string | null | undefined, now: Date = new Date()): number | null {
  if (!iso) return null
  const t = Date.parse(iso)
  if (Number.isNaN(t)) return null
  const start = new Date(t)
  start.setUTCHours(0, 0, 0, 0)
  const end = new Date(now.getTime())
  end.setUTCHours(0, 0, 0, 0)
  return Math.floor((end.getTime() - start.getTime()) / 86_400_000)
}
